import { PreviousWidgetInfo } from 'modules/Editor/store/infographSlice.types';
import { PageId, WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { LineWidgetData } from 'widgets/Lines/LineWidget/LineWidget.types';
import { checkIfLineWidgetHasConnectedPort, generateWidgetId, getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, NewWidget } from 'widgets/Widget.types';

import { AddWidget } from './infographSlice';

export const parseWidgetData = (newWidgetData: NewWidget[] | NewWidget, pageId: PageId): [WidgetId[], AddWidget[]] => {
  const newWidgetIds: WidgetId[] = [];
  const oldToNewWidgetIdMap: { [key: string]: WidgetId } = {}; // Create the mapping object

  const parseNewWidget = (newWidget: NewWidget) => {
    const { widgetType, widgetData, groupWidgets, isResponsiveGroup, copyFrom, customWidgetId, targetWidgetId } =
      newWidget;

    const componentWidgetIdMap: { [key: string]: WidgetId } = {};

    // For groups: create new widget ids for all the group member widgets
    if (groupWidgets && groupWidgets.length) {
      const newMemberWidgetIds: WidgetId[] = [];

      const newGroupWidgets = groupWidgets.map((groupWidget: NewWidget) => {
        const { widgetType, widgetData, componentKey, groupWidgets: nestedGroupWidgets } = groupWidget;
        const widgetId = generateWidgetId(widgetType);
        newMemberWidgetIds.push(widgetId);

        if (isResponsiveGroup && componentKey) {
          componentWidgetIdMap[componentKey] = widgetId;
        }

        // Handle nested groups when there is a responsive widget inside a group
        // Generate new ids for all the members and update the component widget id map
        let newNestedGroupWidgets: { widgetId: WidgetId; pageId: PageId; widgetData: AllWidgetData }[] = [];
        let newNestedMemberWidgetIds: WidgetId[] = [];
        let newComponentWidgetIdMap: { [key: string]: WidgetId } = {};

        if (nestedGroupWidgets && nestedGroupWidgets.length > 0) {
          newNestedGroupWidgets = nestedGroupWidgets.map((nestedWidget) => {
            const { widgetType, widgetData, componentKey, customWidgetId } = nestedWidget;
            const newWidgetId = customWidgetId || generateWidgetId(widgetType);
            newNestedMemberWidgetIds.push(newWidgetId);

            if (componentKey) {
              newComponentWidgetIdMap[componentKey] = newWidgetId;
            }

            return { widgetId: newWidgetId, pageId, widgetData };
          });
        }

        const nestedWidgetData =
          newNestedGroupWidgets.length > 0
            ? {
                ...widgetData,
                memberWidgetIds: newNestedMemberWidgetIds,
                componentWidgetIdMap: newComponentWidgetIdMap,
              }
            : widgetData;

        return {
          widgetId,
          pageId,
          widgetData: nestedWidgetData,
          ...(newNestedGroupWidgets.length > 0 && { groupWidgets: newNestedGroupWidgets }),
        };
      });

      const groupWidgetId = customWidgetId || generateWidgetId(widgetType);
      newWidgetIds.push(groupWidgetId);

      // replace the member widget ids in the group
      const newGroupWidgetData = {
        ...widgetData,
        memberWidgetIds: newMemberWidgetIds,
        ...(isResponsiveGroup && { componentWidgetIdMap }),
      };

      const addGroupWidget = {
        widgetId: groupWidgetId,
        pageId,
        widgetData: newGroupWidgetData,
        groupWidgets: newGroupWidgets,
        isResponsiveGroup,
        copyFrom,
        positionedNextTo: targetWidgetId,
      };

      // Update the oldToNewWidgetIdMap
      if (copyFrom) {
        oldToNewWidgetIdMap[copyFrom] = groupWidgetId;
      }

      return addGroupWidget;
    }

    const widgetId = customWidgetId || generateWidgetId(widgetType);
    newWidgetIds.push(widgetId);

    // Update the oldToNewWidgetIdMap
    if (copyFrom) {
      oldToNewWidgetIdMap[copyFrom] = widgetId;
    }

    const addWidget = { widgetId, pageId, widgetData, copyFrom, positionedNextTo: targetWidgetId };
    return addWidget;
  };

  const addWidgets = Array.isArray(newWidgetData) ? newWidgetData.map(parseNewWidget) : parseNewWidget(newWidgetData);
  const addWidgetsArray = Array.isArray(addWidgets) ? addWidgets : [addWidgets];
  const addWidgetsWithLinesProper = addWidgetsArray.map(
    (addWidget: { widgetId: string; pageId: string; widgetData: AllWidgetData; copyFrom?: string }) => {
      const { widgetId, widgetData, copyFrom } = addWidget;
      const widgetType = getWidgetTypeFromId(widgetId);
      // Look at the copyFrom field of the NewWidget object.
      // If the current widget is a line widget, and the copyFrom field is not null,
      // then find the widget with the id in the startPort and endPort which has the copyFrom equal to those startPort endPort ids.
      // Then, once you find that widget, get the id of that new widget and set it as the startPort or endPort id for the newly
      // created line widget.
      if (widgetType === WidgetType.Line && !!copyFrom && ('startPort' in widgetData || 'endPort' in widgetData)) {
        const { startPort, endPort } = widgetData;
        // Update startPort.id and endPort.id using the copyFromToNewIdMap
        if (startPort) {
          if (startPort.widgetId in oldToNewWidgetIdMap) {
            startPort.widgetId = oldToNewWidgetIdMap[startPort.widgetId];
          } else {
            // Did not copy the attached widget, so remove the port
            widgetData.startPort = null;
          }
        }
        if (endPort) {
          if (endPort.widgetId in oldToNewWidgetIdMap) {
            endPort.widgetId = oldToNewWidgetIdMap[endPort.widgetId];
          } else {
            // Did not copy the attached widget, so remove the port
            widgetData.endPort = null;
          }
        }
      }
      return addWidget;
    },
  );

  return [newWidgetIds, addWidgetsWithLinesProper];
};

// offset for x & y-axis from initial position (px)
const WIDGET_POSITION_OFFSET = 8;

export const offsetWidgetPosition = (
  previousWidgetInfo: PreviousWidgetInfo,
  newWidgetData: NewWidget | NewWidget[],
  activePageId: PageId,
) => {
  // check and bypass if newWidgetData is an array of widgets
  const isMultipleWidgets = Array.isArray(newWidgetData);
  if (isMultipleWidgets) return;

  // bypass if widget is added via drag and drop
  if (newWidgetData?.isDnD || !previousWidgetInfo) return;

  const { widgetType: currWidgetType, widgetData } = newWidgetData;

  if (currWidgetType === WidgetType.Line) {
    const widgetDataAsLine = widgetData as LineWidgetData;
    // check if line widget is added and has a connected end port or start port and bypass if line widget has any port
    if (checkIfLineWidgetHasConnectedPort(widgetDataAsLine)) return;
  }

  const { widgetId: prevWidgetId, pageId: prevPageId, widgetData: prevWidgetData } = previousWidgetInfo;
  const { topPx: prevTopPx, leftPx: prevLeftPx } = prevWidgetData;

  const prevWidgetType = getWidgetTypeFromId(prevWidgetId);
  const isSameWidgetType = prevWidgetType === currWidgetType;
  const isSamePage = prevPageId === activePageId;

  // compare previous and current widget type and check if they are on the same page
  if (isSameWidgetType && isSamePage) {
    // offset position by 8px from top and 8 px from the right
    newWidgetData.widgetData = {
      ...newWidgetData.widgetData,
      ...{ topPx: prevTopPx + WIDGET_POSITION_OFFSET, leftPx: prevLeftPx + WIDGET_POSITION_OFFSET },
    };
  }
};
